import type { EmailProviderStatusDTO, TestEmailResult } from '@hearth/shared';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, Send, ShieldCheck, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Badge, Button, Card, CardBody, CardHeader, Field, Input, useToast } from '../../../components/ui';
import { useAuth } from '../../../hooks/useAuth';
import { api, errorMessage } from '../../../lib/api';
import { GroupsSection } from './GroupsSection';
import { MailboxesCard } from './MailboxesCard';

/**
 * Provider credentials live in the server environment (never in the database or the browser). This screen
 * shows what is configured, checks it, and sends a real test message.
 */
export function EmailSection() {
  const { user } = useAuth();
  const toast = useToast();
  const [to, setTo] = useState(user?.email ?? '');
  const { data } = useQuery({ queryKey: ['settings', 'email-provider'], queryFn: ({ signal }) => api.get<EmailProviderStatusDTO>('/api/settings/email-provider', { signal }) });
  const verify = useMutation({ mutationFn: () => api.post<EmailProviderStatusDTO>('/api/settings/email-provider/verify'), onError: (error) => toast.error(errorMessage(error)) });
  const test = useMutation({
    mutationFn: () => api.post<TestEmailResult>('/api/settings/email-provider/test', { to }),
    onSuccess: (result) => (result.outcome === 'sent' ? toast.success(`Test email accepted by the provider${data?.provider === 'log' ? ' (log provider: recorded, not delivered)' : ''}`) : toast.error(result.error ?? result.outcome)),
    onError: (error) => toast.error(errorMessage(error)),
  });
  const check = verify.data?.verify;

  return (
    <>
      <MailboxesCard />
      <Card>
        <CardHeader title="Organization sender" description="Set EMAIL_PROVIDER and its credentials in the server environment, then restart the API and worker." />
        <CardBody className="space-y-3 text-[13px]">
          {data && (
            <dl className="grid grid-cols-[10rem_minmax(0,1fr)] gap-y-1">
              <dt className="text-slate-500">Provider</dt>
              <dd>
                {data.provider} {data.configured ? <Badge color="green" size="sm">Configured</Badge> : <Badge color="red" size="sm">Not configured</Badge>}
                {data.provider === 'log' && <span className="ml-2 text-amber-700">Records emails without delivering them — for evaluation only.</span>}
              </dd>
              <dt className="text-slate-500">Sends from</dt>
              <dd>
                {data.fromName} &lt;{data.fromAddress}&gt;
              </dd>
              <dt className="text-slate-500">Webhook URL</dt>
              <dd className="break-all">
                <code>{data.webhookUrl}?token=…</code> {data.webhookSecretConfigured ? <Badge color="green" size="sm">Secret set</Badge> : <Badge color="amber" size="sm">EMAIL_WEBHOOK_SECRET missing</Badge>}
              </dd>
            </dl>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <Button icon={ShieldCheck} loading={verify.isPending} onClick={() => verify.mutate()}>
              Check connection
            </Button>
            {check && (
              <span className={check.ok ? 'flex items-center gap-1 text-emerald-700' : 'flex items-center gap-1 text-red-700'}>
                {check.ok ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />} {check.message}
              </span>
            )}
            {verify.data && !check && <span className="text-slate-500">This provider has no connection check.</span>}
          </div>
          <form
            className="flex items-end gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              test.mutate();
            }}
          >
            <Field label="Send a test email to">
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Button type="submit" icon={Send} loading={test.isPending} disabled={!data?.configured || !to}>
              Send test
            </Button>
          </form>
        </CardBody>
      </Card>
      <GroupsSection keys={['sending']} />
    </>
  );
}
